/** The Edit properties dialog (Sensor info, redesign phase 6): a draft of
 *  strings for the inputs, the checks the browser can make before Save, and
 *  the patch of what changed; and what Properties shows of where a sensor
 *  is. Since the owner's review of 2026-10-22 a sensor's position is shown
 *  and edited as its UTM survey only (northing, easting, elevation and the
 *  zone): latitude, longitude and height are no longer on the page, though
 *  the api still stores them and the map still reads a stored pair. */
import type { Sensor, SensorPatch, SensorProperties, SensorType } from '../api/types'
import { utmEpsg, utmZoneFor, utmZoneOf, wgs84ToUtm } from './geo'

export interface SensorDraft {
  sensor_type: SensorType | ''
  northing_m: string
  easting_m: string
  elevation_m: string
  crs_epsg: '' | '32638' | '32637' | '4326'
  active: '' | 'yes' | 'no'
  reference_mode: '' | 'oldest' | 'day'
  reference_day: string
  /** Clear a stored latitude and longitude even though the survey beside
   *  them is unchanged, so the map places the sensor by the survey that
   *  Properties shows (canPlaceBySurvey). */
  place_by_survey: boolean
  /** The person has seen that the survey lands far from the stored latitude
   *  and longitude it replaces, and saves it all the same (pairWarning). */
  confirm_far: boolean
}

export const METRES = ['northing_m', 'easting_m', 'elevation_m'] as const
export type MetreField = typeof METRES[number]

/** The grids a survey is entered in. WGS 84 (EPSG:4326) is in degrees, so
 *  it is no grid for a northing and easting in metres; it was offered while
 *  latitude and longitude sat beside them, and a sensor that stored it still
 *  shows it (crsChoices), but it is not offered any more. */
export const CRS_OPTIONS: { value: SensorDraft['crs_epsg']; label: string }[] = [
  { value: '', label: 'Not set' },
  { value: '32638', label: crsLabel(32638) },
  { value: '32637', label: crsLabel(32637) },
]

export function crsLabel(epsg: number): string {
  if (epsg === 4326) return 'WGS 84 (EPSG:4326)'
  const zone = utmZoneOf(epsg)
  return zone === null ? `EPSG:${epsg}` : `WGS 84 / UTM zone ${zone}N (EPSG:${epsg})`
}

/** The dialog's choices: the UTM zones, and the stored grid when it is not
 *  one of them, so the select names what is stored instead of silently
 *  showing Not set over it. */
export function crsChoices(stored: SensorProperties['crs_epsg']): typeof CRS_OPTIONS {
  const value = stored === null ? '' : (String(stored) as SensorDraft['crs_epsg'])
  return CRS_OPTIONS.some((o) => o.value === value)
    ? CRS_OPTIONS : [...CRS_OPTIONS, { value, label: crsLabel(stored as number) }]
}

const isUtm = (crs: SensorDraft['crs_epsg']) => crs !== '' && utmZoneOf(Number(crs)) !== null

const metres = (v: number | null) => (v === null ? '' : String(v))

export function draftFrom(p: SensorProperties): SensorDraft {
  return {
    sensor_type: p.sensor_type ?? '',
    northing_m: metres(p.northing_m), easting_m: metres(p.easting_m),
    elevation_m: metres(p.elevation_m),
    crs_epsg: p.crs_epsg === null ? '' : (String(p.crs_epsg) as SensorDraft['crs_epsg']),
    active: p.active === null ? '' : p.active ? 'yes' : 'no',
    reference_mode: p.reference_mode ?? '',
    reference_day: p.reference_day ?? '',
    place_by_survey: false,
    confirm_far: false,
  }
}

/** The draft with one input changed. A confirmation that the survey may
 *  land far from the stored pair answers the numbers it was given for, so a
 *  changed northing, easting or zone asks again. */
export function withField<K extends keyof SensorDraft>(d: SensorDraft, k: K, v: SensorDraft[K]): SensorDraft {
  const next = { ...d, [k]: v }
  if (k === 'northing_m' || k === 'easting_m' || k === 'crs_epsg') next.confirm_far = false
  return next
}

/** A number in metres as typed, spaces allowed. A comma is taken only as a
 *  thousands separator in a number with a decimal point (4,706,117.527): a
 *  lone comma is a decimal comma in Georgian and Russian practice, and read
 *  as thousands it would store 12,5 as 125 m, so it is refused instead
 *  (review finding 1). */
export function parseMetres(text: string): number | null | 'invalid' {
  const clean = text.replace(/\s/g, '')
  if (clean === '') return null
  if (clean.includes(',')) {
    if (!/^-?\d{1,3}(,\d{3})+\.\d+$/.test(clean)) return 'invalid'
    return Number(clean.replace(/,/g, ''))
  }
  return /^-?\d+(\.\d+)?$/.test(clean) ? Number(clean) : 'invalid'
}

/** Whether the northing, easting or zone differs from what is stored. */
function surveyChanged(d: SensorDraft, original: SensorProperties): boolean {
  const before = draftFrom(original)
  return d.northing_m.trim() !== before.northing_m || d.easting_m.trim() !== before.easting_m
    || d.crs_epsg !== before.crs_epsg
}

/** A northing, an easting and the UTM zone they are in: what the map needs
 *  to place a sensor by its survey (services/geo.py `position`). */
function surveyComplete(d: SensorDraft): boolean {
  return typeof parseMetres(d.northing_m) === 'number' && typeof parseMetres(d.easting_m) === 'number'
    && isUtm(d.crs_epsg)
}

/** A whole UTM survey is stored, so the map could place the sensor by it if
 *  the latitude and longitude stored beside it were cleared. */
export function canPlaceBySurvey(p: SensorProperties): boolean {
  return (p.latitude !== null || p.longitude !== null) && surveyComplete(draftFrom(p))
}

/** Whether Save clears the stored latitude and longitude. The map places a
 *  sensor at them when both are stored and at its survey only otherwise,
 *  and neither is on this page any more: a survey saved over them would
 *  leave the sensor on the map where it used to be, with nothing here saying
 *  why. So a changed survey that can place the sensor clears them, as does
 *  the dialog's Place on the map by this survey for an unchanged one. */
export function clearsPair(d: SensorDraft, original: SensorProperties): boolean {
  return (original.latitude !== null || original.longitude !== null)
    && (surveyChanged(d, original) || d.place_by_survey) && surveyComplete(d)
}

/** How far a survey may land from the latitude and longitude it replaces
 *  before Save asks. The pair converts to the survey within centimetres
 *  (shownSurvey), so a kilometre is no rounding but another place: most
 *  often the neighbouring zone, which puts the sensor some 500 km off, or a
 *  dropped digit. */
export const FAR_M = 1000

const kilometres = (m: number) => (m < 10_000 ? (m / 1000).toFixed(1) : Math.round(m / 1000).toLocaleString('en-US'))

/** What Save would do to the map when the survey that clears the stored
 *  pair lands more than FAR_M from it, or null. The pair is the only record
 *  of the old position (the audit row keeps the new values), so a wrong zone
 *  or number here would lose it: Save waits for confirm_far. */
export function pairWarning(d: SensorDraft, original: SensorProperties): string | null {
  if (!clearsPair(d, original) || original.latitude === null || original.longitude === null) return null
  const zone = utmZoneOf(Number(d.crs_epsg))!
  const pair = wgs84ToUtm(original.latitude, original.longitude, zone)
  if (!pair) return null
  const off = Math.hypot(Number(parseMetres(d.easting_m)) - pair.easting,
                         Number(parseMetres(d.northing_m)) - pair.northing)
  if (off <= FAR_M) return null
  const home = utmZoneFor(original.longitude)
  return home === zone
    ? `This northing and easting lie ${kilometres(off)} km from the stored latitude and longitude. `
      + 'Saving clears them. Check the numbers.'
    : `In UTM zone ${zone}N this northing and easting lie ${kilometres(off)} km from the stored latitude `
      + `and longitude, which are in zone ${home}N. Saving clears them. Check the zone.`
}

/** The checks Save waits for. A survey is checked as a whole only when it
 *  is being changed: an older sensor may store half of one, or a grid in
 *  degrees, and must still take a new type or note without being made to
 *  mend that first. */
export function draftErrors(d: SensorDraft, original: SensorProperties): Partial<Record<keyof SensorDraft, string>> {
  const errors: Partial<Record<keyof SensorDraft, string>> = {}
  for (const field of METRES) {
    if (parseMetres(d[field]) === 'invalid') errors[field] = 'A number in metres, with a dot for decimals.'
  }
  if (surveyChanged(d, original)) {
    const northing = parseMetres(d.northing_m)
    const easting = parseMetres(d.easting_m)
    if (northing === null && easting !== null) errors.northing_m = 'Give the northing with the easting.'
    if (easting === null && northing !== null) errors.easting_m = 'Give the easting with the northing.'
    if ((northing !== null || easting !== null) && !isUtm(d.crs_epsg)) {
      errors.crs_epsg = 'Pick the UTM zone of the northing and easting.'
    }
  }
  const far = pairWarning(d, original)
  if (far && !d.confirm_far) errors.confirm_far = far
  if (d.reference_mode === 'day' && !d.reference_day) errors.reference_day = 'Pick the reference day.'
  return errors
}

/** Only what changed, so a save names what the person touched and the
 *  audit row says the same, and the pair a survey replaces (clearsPair). */
export function patchFrom(d: SensorDraft, original: SensorProperties): SensorPatch {
  const before = draftFrom(original)
  const patch: SensorPatch = {}
  if (d.sensor_type !== before.sensor_type) patch.sensor_type = d.sensor_type || null
  for (const field of METRES) {
    if (d[field].trim() !== before[field]) {
      const value = parseMetres(d[field])
      patch[field] = value === 'invalid' ? null : value
    }
  }
  if (d.crs_epsg !== before.crs_epsg) {
    patch.crs_epsg = d.crs_epsg ? (Number(d.crs_epsg) as SensorPatch['crs_epsg']) : null
  }
  // In the same save as the survey, so the map never shows the old place
  // beside the new numbers (the api parses a null as nothing typed and
  // stores NULL, services/sensors.py check_patch).
  if (clearsPair(d, original)) {
    patch.latitude = null
    patch.longitude = null
  }
  if (d.active !== before.active) patch.active = d.active === '' ? null : d.active === 'yes'
  if (d.reference_mode !== before.reference_mode || d.reference_day !== before.reference_day) {
    patch.reference_mode = d.reference_mode || null
    patch.reference_day = d.reference_mode === 'day' ? d.reference_day : null
  }
  return patch
}

export interface ShownSurvey {
  northing: number | null
  easting: number | null
  epsg: number | null
  /** The northing and easting are worked out here, not stored. */
  converted: boolean
}

/** The survey Properties shows: the stored one, or, for a sensor that has a
 *  latitude and longitude but no northing or easting, those converted to
 *  UTM, so every sensor reads in the one grid. The zone is always the one
 *  the longitude falls in (37N west of 42° E, 38N from it), never a grid
 *  the sensor happens to store without a survey: a pair converted into the
 *  neighbouring zone is a valid number but not the one a surveyor there
 *  would write down. */
export function shownSurvey(p: SensorProperties): ShownSurvey {
  if (p.northing_m === null && p.easting_m === null && p.latitude !== null && p.longitude !== null) {
    const utm = wgs84ToUtm(p.latitude, p.longitude)
    if (utm) return { northing: utm.northing, easting: utm.easting, epsg: utmEpsg(utm.zone), converted: true }
  }
  return { northing: p.northing_m, easting: p.easting_m, epsg: p.crs_epsg, converted: false }
}

/** The quiet line under Properties: where the numbers came from and where
 *  the map puts the sensor, which is the api's verdict (`position`). */
export function positionNote(shown: ShownSurvey, position: Sensor['position']): string {
  if (shown.converted) {
    return 'Northing and easting converted from the stored latitude and longitude, where the map places the sensor.'
  }
  if (!position) return 'Not on the map: give it a northing, an easting and their UTM zone.'
  return position.source === 'utm' ? 'On the map at this UTM survey.' : 'On the map at its stored latitude and longitude.'
}

/** The Edit properties dialog's note while a latitude and longitude are
 *  stored: they are not on the page, so it says what a saved survey does to
 *  them (patchFrom clears them) and, when Properties showed a converted
 *  survey, that the empty inputs are not a lost one and which zone those
 *  numbers are in: the zone select shows what is stored, which for such a
 *  sensor may be the other zone or none. Null otherwise. */
export function surveyNote(p: SensorProperties): string | null {
  if (p.latitude === null && p.longitude === null) return null
  const shown = shownSurvey(p)
  return shown.converted
    ? 'Properties shows a northing and easting converted from the stored latitude and longitude into '
      + `UTM zone ${utmZoneOf(shown.epsg)}N, the zone the longitude falls in. No survey is stored. Saving `
      + 'a northing and easting with their UTM zone stores them and clears the latitude and longitude, '
      + 'so the map places the sensor at the survey.'
    : 'Saving a changed northing, easting or zone clears the stored latitude and longitude, so the map '
      + 'places the sensor at the survey instead.'
}

/** "13 Oct 2026" for a reference day. */
export function formatDay(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number)
  const month = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov',
    'Dec'][(m ?? 1) - 1]
  return `${d} ${month} ${y}`
}
